import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError
} from '@reduxjs/toolkit/query/react'
import { RootState } from '@/application/store'
import { API_CONSTANTS } from '../constants/api.constants'
import { logout } from '../slices/auth/auth.slice'

const baseQueryWithAuth = (baseUrl: string) => {
  const baseQuery = fetchBaseQuery({
    baseUrl,
    timeout: 0, // No timeout - wait indefinitely for response
    prepareHeaders: (headers, { getState }) => {
      const accessToken = (getState() as RootState).auth.accessToken

      if (accessToken) {
        headers.set('authorization', `Bearer ${accessToken}`)
      }

      return headers
    }
  })

  const baseQueryWithInterceptor: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError
  > = async (args, api, extraOptions) => {
    const result = await baseQuery(args, api, extraOptions)

    // Handle 401 Unauthorized - redirect to logout
    if (result.error && result.error.status === 401) {
      // Don't redirect if already on login page (to allow form validation errors)
      const currentPath = window.location.pathname
      if (currentPath === '/login' || currentPath === '/registration-npsn') {
        return result
      }

      // Dispatch logout action to clear auth state
      api.dispatch(logout())

      // Redirect to login page
      window.location.href = '/login'
    }

    return result
  }

  return baseQueryWithInterceptor
}

export const authApi = createApi({
  reducerPath: 'authApi',
  tagTypes: ['Auth', 'User'],
  baseQuery: baseQueryWithAuth(API_CONSTANTS.AUTH_BASE_URL),
  endpoints: () => ({})
})

export const api = createApi({
  reducerPath: 'api',
  tagTypes: ['User', 'Resource', 'MasterSchool', 'SchoolSync'],
  baseQuery: baseQueryWithAuth(API_CONSTANTS.CORE_BASE_URL),
  endpoints: () => ({})
})

export const publicApi = createApi({
  reducerPath: 'publicApi',
  tagTypes: ['Region', 'School'],
  baseQuery: fetchBaseQuery({
    baseUrl: API_CONSTANTS.PUBLIC_BASE_URL,
    timeout: 0 // No timeout - wait indefinitely for response
  }),
  endpoints: () => ({})
})
